declare global {
  var jsPDF: any;
}

export enum ProjectTheme {
  Theme1 = "theme1",
}

export enum SpaceType {
  System    = "system", // For future admin stuff
  Room      = "room",
  Workspace = "workspace",
  Project   = "project",
}

export interface SpaceSettings {
  online?:  boolean;
  daily_summary?:  boolean;
  display_as?:     "name" | "title" | "initials";
  during_meeting?: boolean;
  hide_in_list?:   boolean;
  hide_in_matrix?: boolean;
  id?:             number;
  initials?:       string;
  mentions?:       boolean;
  news_feed?:      NewsFeedSettings;
  participation?:  boolean;
  reminder?:       boolean;
  role?:           UserRole;
  space_id?:       number;
  space_type?:     SpaceType;
  status?:         UserStatus;
  title?:          string;
  user_changes?:   boolean;
  watching?:       boolean;
  workspace_id?:   number;
}
export enum NewsFeedSettings {
  None     = "none",
  Affected = "affected",
  All      = "all",
}

export enum UserType {
  Accountless = "accountless",
  Default     = "default",
  Group       = "group",
}

export enum FileType {
  Avatar     = 'avatar',
  Attachment = 'attachment',
  Document   = 'document',
}

export type LogDataType  = ItemType | DataType.User | DataType.Space;
export type LogEventType = 'created' | 'changed' | 'deleted' | 'leave' | 'enter' | 'start' | 'close';
export type LogType      = "question" | "deliverable" | "action";
export enum LogActions {
  JoinRoom     = "join_room",
  LeaveRoom    = "leave_room",
  JoinMeeting  = "join_meeting",
  LeaveMeeting = "leave_meeting",
}

export type Avatar = {
  mime: string;
  uri:  string;
}

export enum UserStatus {
  Accepted  = "accepted", // User that successfully added to space and they have logged in
  Invited   = "invited", // User was invited to join a space
  Requested = "requested", // User requested access to that space
  Created   = "created", // Accountless user created to that space
}

export enum UserRole {
  Owner  = "owner",
  Leader = "leader",
  Viewer = "viewer",
  Member = "member",
}

/**
   * Priority in order as given. So Answered will overwrite Critical.
   */
export enum ItemStatus {
  New      = "new",        // question.id unset
  Active   = "active",     // question or deliverable is active
  Archived = "archived",   // question.archived 
  Answered = "answered",   // question.answered set
  Critical = "critical",   // question.critical set
}

export enum DeliverablePosition {
  Board     = "board",
  Delivered = "delivered",
  Done      = "done",
}

export enum QuestionPosition {
  Board    = "board",
  Before   = "before",
  During   = "during",
  Remitted = "remitted",
  Deferred = "deferred",
  Future   = "future",
  Decided  = "decided",
}

export type Filter = {
  [prop: string]: any
}

export enum DataType {
  Agenda       = "agenda",
  Log          = "log",
  Comment      = "comment",
  Deliverable  = "deliverable",
  File         = "file",
  Group        = "group",
  Item         = "item",
  Meeting      = "meeting",
  Milestone    = "milestone",
  Notification = "notification",
  Question     = "question",
  Space        = "space",
  Tag          = "tag",
  User         = "users",
}
export type ItemType = DataType.Question | DataType.Deliverable | DataType.Meeting | DataType.Milestone;
export type FeedbackType = "idea" | "support" | "bug_report";
export type Feedback = {
  type:    FeedbackType;
  message: string;
  files?:  any[];
};

export enum Colors {
  Primary   = "#34515e",
  Secondary = "#60738B",
  Black     = "#292b2c",
  White     = "#FFFFFFF",
  Gray      = "#777",
}

export enum LoadingStatus {
  Loading  = "loading",
  Finished = "finished",
  None     = "none",
  Next     = "next",    // Waiting for next page 
  Waiting  = "waiting", // waiting for query data to load
}


export enum Services {
  Account      = "account",
  Agenda       = "agenda",
  AgendaSpace  = "agenda-space",
  Chat         = "chat",
  Comment      = "comment",
  Deliverable  = "deliverable",
  Email        = "email",
  Feedback     = "feedback",
  File         = "file",
  GroupUsers   = "group-users",
  Invite       = "invite",
  ICS          = "ics",
  Item         = "item",
  ItemItem     = "item-item",
  ItemUsers    = "item-users",
  Log          = "log",
  Meeting      = "meeting",
  Milestone    = "milestone",
  Question     = "question",
  Reaction     = "comment-reaction",
  Space        = "space",
  SpaceUsers   = "space-users",
  Tag          = "tag",
  TagItem      = "tag-item",
  TagSpace     = "tag-space",
  Storage      = "storage",
  Session      = "session",
  Users        = "users",
  Avatar       = "avatar",
  MeetingICS   = "meeting-ics",
  MeetingInvitation   = "meeting-invitation",
}

export enum CommentReaction {
  Up   = "up",
  Down = "down",
}

// Supported languages
export enum Language {
  EN = "en",
  SV = "sv"
}

export enum MeetingCategory {
  Projection     = "projection",
  Planning       = "planning",
  Workshop       = "workshop",
  Administration = "administration",
  Decision       = "decision",
  Coordination   = "coordination",
  Construction   = "construction",
  Technical      = "technical",
  Economy        = "economy",
  Working        = "working",
  Installation   = "installation",
  Summary        = "summary",
  Reconciliation = "reconciliation",
}

export type MeetingAttendee = {
  user_id: number;
  in?: any;
  out?: number;
}

export enum MeetingType {
  GoogleMeeting   = "google_meet",
  MicrosoftTeams  = "microsoft_teams",
  Slack           = "slack",
  Zoom            = "zoom",
  Skype           = "skype",
  Phone           = "phone",
  Other           = "other",
  None            = "none",
}

/**
 * Used as type for the @data prop of a log entry.
 * The prop is 
 * - NULL for action events, 
 * 
 * - a single value of type string | number | boolean for @event_type 'changed',
 *   i.e. a single value change 
 *   Note: Dates are stored as ISOStrings
 * 
 * - Objects in several cases:
 *  o an Item was Created: the whole data of the created item is stored in @data
 *  o a link was created or removed. In this case @item_id holds the id of the main item 
 *    and @data holds an object {user_id/item_id: number} with the information about 
 *    which data was linked to. 
 *    Note: This implies that two rows are created for an item_item link!
 */
export type JSONValue = 
  | string
  | number
  | boolean
  | null
  | JSONValue[]
  | {[key: string]: JSONValue}


export const QuestionLogProps: {[key: string]: null} = {
  "number":     null,
  "from":       null,
  "to":         null,
  "title":      null,
  "created_at": null,
  "status":     null,
  "date":       null,
};

export const DelivLogProps: {[key: string]: null} = {
  "number":     null,
  "start_date": null,
  "delay_date": null,
  "to":         null,
  "title":      null,
  "created_at": null,
  "status":     null,
  "date":       null,
};

export const ActionLogProps: {[key: string]: null} = {
  "created_at": null,
  "user":       null,
  "action":     null,
};
export type RoomLogProps = keyof typeof QuestionLogProps | keyof typeof DelivLogProps | keyof typeof ActionLogProps;